/**
 * Non-React glue for the Shortlist Django navbar entry point.
 *
 * This duplicates some of the functionality of the useShortlist hook to show
 * the count badge, this duplication helps us to avoid depending on React.
 *
 * Responsibilities:
 *  - Render the shortlist count badge from `localStorage.shortlist.opks.length`.
 *  - Keep the badge in sync across tabs via the `storage` event (sitters open
 *    in new tabs, so the count can change without a navigation), and within
 *    this tab via `SHORTLIST_CHANGE_EVENT` (the React island mutates the
 *    shortlist without a navigation either).
 *  - Keep the button's accessible name carrying the count, since the badge
 *    itself is `aria-hidden` (a bare digit read out of context is noise).
 *  - On click, lazy-load the React modal island and dispatch the custom open
 *    event it listens for.
 */
import { plural, t } from '@lingui/core/macro';

import {
  SHORTLIST_CHANGE_EVENT,
  SHORTLIST_MODAL_OPEN_EVENT,
  SHORTLIST_STORAGE_KEY,
} from '@rover/components/src/shortlist/constants';

const BUTTON_SELECTOR = '.js-shortlist-navbar-button';
const BADGE_SELECTOR = '.js-shortlist-navbar-badge';

const getShortlistCount = (): number => {
  try {
    const raw = localStorage.getItem(SHORTLIST_STORAGE_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.opks) ? parsed.opks.length : 0;
  } catch {
    return 0;
  }
};

// The visible label stays a prefix of the accessible name so the button still
// satisfies WCAG 2.5.3 Label in Name.
const labelForCount = (count: number): string =>
  count === 0
    ? t`Contact list`
    : plural(count, { one: 'Contact list, # contact', other: 'Contact list, # contacts' });

const updateNavbarButtons = (): void => {
  const count = getShortlistCount();
  for (const badge of document.querySelectorAll<HTMLElement>(BADGE_SELECTOR)) {
    badge.textContent = count > 0 ? String(count) : '';
    badge.hidden = count === 0;
  }
  for (const button of document.querySelectorAll<HTMLElement>(BUTTON_SELECTOR)) {
    button.setAttribute('aria-label', labelForCount(count));
  }
};

const dispatchOpen = (): void => {
  window.dispatchEvent(new CustomEvent(SHORTLIST_MODAL_OPEN_EVENT));
};

let modalLoaded = false;
const openModal = (): void => {
  if (modalLoaded) {
    dispatchOpen();
    return;
  }
  import('@rover/components/src/shortlist/mount')
    .then(() => {
      modalLoaded = true;
      dispatchOpen();
      return undefined;
    })
    .catch(() => {
      // Allow a retry on the next click if the chunk failed to load.
    });
};

// The authenticated header is re-rendered client-side from a Mustache template
// (app/js/Authenticated.js does `$('.js-anonymous-header').html(...)`), which
// destroys and recreates the button. So we must not bind to the button node
// directly — it would be discarded on that re-render. Instead delegate clicks
// from `document`, which works for both the server-rendered (anonymous) button
// and the Mustache-recreated (authenticated) one, regardless of timing.
document.addEventListener('click', (event) => {
  const { target } = event;
  if (target instanceof Element && target.closest(BUTTON_SELECTOR)) {
    openModal();
  }
});

// Cross-tab sync: another tab may add/remove sitters.
window.addEventListener('storage', (event) => {
  if (event.key === SHORTLIST_STORAGE_KEY) {
    updateNavbarButtons();
  }
});

// Same-tab sync: the React island on this page (the modal, its recommendations
// carousel) mutates the shortlist, which fires no `storage` event here.
window.addEventListener(SHORTLIST_CHANGE_EVENT, updateNavbarButtons);

// Paint server-rendered buttons present at load.
updateNavbarButtons();

// The Mustache re-render recreates the button with the static server-rendered
// label and an empty badge (the count is intentionally client-side only, not in
// the template context), so repaint whenever the header's direct children
// change. `childList` without `subtree` is important: our own textContent/hidden
// writes mutate the badge's descendants, not the <ul>'s direct children, so this
// observer can't trigger itself into a loop.
document.querySelectorAll<HTMLElement>('.js-anonymous-header').forEach((header) => {
  new MutationObserver(updateNavbarButtons).observe(header, { childList: true });
});
